import {
  EditorAnimationParams,
  EditorCropCommittedEdit,
  EditorResizeCommittedEdit,
  EditorRotateCommittedEdit,
} from '../../domain/editor-domain.models';
import { normalizeRect, rotateSize, Size2D } from '../../domain/interfaces/shared';
import { clamp } from './canvas-color.utils';
import { cloneCanvas, createCanvas } from './canvas-dom.utils';

export type EditorCropParams = EditorCropCommittedEdit['params'];
export type EditorResizeParams = EditorResizeCommittedEdit['params'];
export type EditorRotateParams = EditorRotateCommittedEdit['params'];

/**
 * Mirrors the backend frame assembly (AnimateExtensions.Resize +
 * ImageEditorService.Resize with KeepAspectRatio): every frame is
 * aspect-fitted into the animation width/height, centered, and the padding
 * is filled with the background color (or left transparent). This keeps the
 * canvas image size constant across frames of different sizes, so the
 * viewport fit and the crop overlay stay still while the animation plays.
 */
export function applyAnimationFrameBox(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  params: EditorAnimationParams,
): HTMLCanvasElement {
  const boxWidth = Math.round(params.width);
  const boxHeight = Math.round(params.height);

  if (boxWidth <= 0 || boxHeight <= 0) {
    return cloneCanvas(document, sourceCanvas);
  }

  const target = createCanvas(document, boxWidth, boxHeight);
  const ctx = target.getContext('2d');
  if (!ctx) {
    return sourceCanvas;
  }

  if (params.backgroundColor) {
    ctx.fillStyle = params.backgroundColor;
    ctx.fillRect(0, 0, boxWidth, boxHeight);
  }

  const scale = Math.min(boxWidth / sourceCanvas.width, boxHeight / sourceCanvas.height);
  const drawWidth = Math.max(1, Math.round(sourceCanvas.width * scale));
  const drawHeight = Math.max(1, Math.round(sourceCanvas.height * scale));

  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(
    sourceCanvas,
    Math.round((boxWidth - drawWidth) / 2),
    Math.round((boxHeight - drawHeight) / 2),
    drawWidth,
    drawHeight,
  );

  return target;
}

export function applyCrop(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  params: EditorCropParams,
): HTMLCanvasElement {
  const normalizedRect = normalizeRect(params.rect);
  if (!normalizedRect) {
    return cloneCanvas(document, sourceCanvas);
  }

  const sourceX = clamp(Math.round(normalizedRect.x), 0, sourceCanvas.width - 1);
  const sourceY = clamp(Math.round(normalizedRect.y), 0, sourceCanvas.height - 1);
  const width = clamp(Math.round(normalizedRect.width), 1, sourceCanvas.width - sourceX);
  const height = clamp(Math.round(normalizedRect.height), 1, sourceCanvas.height - sourceY);

  const target = createCanvas(document, width, height);
  const ctx = target.getContext('2d');
  if (!ctx) {
    return sourceCanvas;
  }

  ctx.drawImage(sourceCanvas, sourceX, sourceY, width, height, 0, 0, width, height);
  return target;
}

export function applyResize(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  params: EditorResizeParams,
): HTMLCanvasElement {
  const targetWidth = Math.max(1, Math.round(sourceCanvas.width * params.scaleX));
  const targetHeight = Math.max(1, Math.round(sourceCanvas.height * params.scaleY));
  const target = createCanvas(document, targetWidth, targetHeight);
  const ctx = target.getContext('2d');

  if (!ctx) {
    return sourceCanvas;
  }

  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(sourceCanvas, 0, 0, targetWidth, targetHeight);
  return target;
}

export function applyRotate(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  params: EditorRotateParams,
): HTMLCanvasElement {
  const inputSize: Size2D = {
    width: sourceCanvas.width,
    height: sourceCanvas.height,
  };

  const outputSize = getRotateOutputSize(inputSize, params);
  const target = createCanvas(document, outputSize.width, outputSize.height);
  const ctx = target.getContext('2d');

  if (!ctx) {
    return sourceCanvas;
  }

  ctx.translate((outputSize.width - inputSize.width) / 2, (outputSize.height - inputSize.height) / 2);
  ctx.translate(inputSize.width / 2, inputSize.height / 2);
  ctx.rotate(((params.rightAngle + params.preciseAngle) * Math.PI) / 180);
  ctx.scale(params.flipX ? -1 : 1, params.flipY ? -1 : 1);
  ctx.translate(-inputSize.width / 2, -inputSize.height / 2);
  ctx.drawImage(sourceCanvas, 0, 0);

  return target;
}

export function getRotateOutputSize(size: Size2D, params: EditorRotateParams): Size2D {
  const rightAngle = ((params.rightAngle % 360) + 360) % 360;
  const hasPrecise = params.preciseAngle !== 0;

  if ((params.cut || !hasPrecise) && (!rightAngle || rightAngle === 180)) {
    return { ...size };
  }

  let output = { ...size };

  if (!params.cut && hasPrecise) {
    output = rotateSize(output, params.preciseAngle);
  }

  if (rightAngle && rightAngle !== 180) {
    output = {
      width: output.height,
      height: output.width,
    };
  }

  return {
    width: Math.max(1, Math.round(output.width)),
    height: Math.max(1, Math.round(output.height)),
  };
}
